import { z } from 'zod';
import { DetectedIssue } from '@hud/rules-dtos/types';
import { PublicIssue as LegacyPublicIssue, IssueValue } from './issues.dtos';
import { ExplicitTimeFrame } from './webapp-common.dto';
import { MachineMetrics, ObservabilityIdentifiers } from '@hud/exemplar-dtos';

export const Issue = DetectedIssue.extend({
  id: z.number(),
});

export type Issue = z.infer<typeof Issue>;

export const EndpointInvestigationException = z.object({
  name: z.string(),
  message: z.string().optional(),
  functions: z.array(
    z.object({
      id: z.number(),
      signature: z.string(),
      filePath: z.string(),
    }),
  ),
});

export type EndpointInvestigationException = z.infer<
  typeof EndpointInvestigationException
>;

// similar to the S3EndpointInvestigation from @hud/exemplar-dtos, but with function signature instead of function_id and without the irrelevant fields like version
export const ExemplarData = z.object({
  context: z.object({
    type: z.literal('http').optional().default('http'),
    method: z.string(),
    params: z.unknown(),
    query: z.unknown(),
    request_body: z.unknown(),
    route: z.string(),
    status_code: z.number(),
    pod_name: z.string().optional(),
    node_name: z.string().optional(),
    machine_metrics: MachineMetrics.optional(),
    observability_identifiers: ObservabilityIdentifiers.optional(),
  }),
  exceptions: z.array(
    z.object({
      executionFlow: z.array(
        z.object({
          caller_function_signature: z.string().optional(),
          function_signature: z.string(),
        }),
      ),
      message: z.string().optional(),
      name: z.string(),
      stackTrace: z.string().optional(),
    }),
  ),
});

export type ExemplarData = z.infer<typeof ExemplarData>;

export const Exemplar = z.object({
  type: z.literal('endpoint_investigation'),
  timestamp: z.coerce.date(),
  exceptions: z.array(EndpointInvestigationException),
  data: ExemplarData,
});

export type Exemplar = z.infer<typeof Exemplar>;

export const PublicIssue = LegacyPublicIssue.pick({
  id: true,
  serviceId: true,
  serviceName: true,
  detectionTime: true,
  mainEntity: true, // check
  sources: true, // check
  shortTitle: true,
  title: true,
}).extend({
  values: z.array(IssueValue).default([]),
  ruleId: z.string(),
  timeFrame: ExplicitTimeFrame,
  exemplars: z.array(Exemplar).default([]),
  fixItPrompt: z.string().optional(),
});

export type PublicIssue = z.infer<typeof PublicIssue>;

export const IssuesResponse = z.object({
  issues: z.array(PublicIssue),
});

export type IssuesResponse = z.infer<typeof IssuesResponse>;
